import React, { useState, useRef } from 'react';
import './Contact.css';
import { coursesData } from '../../utils/coursesData';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaPaperPlane, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube } from 'react-icons/fa';
import { socialData } from '../../utils/socialData';

const Contact = () => {
  const [formData, setFormData] = useState({
    FullName: '',
    Phone: '',
    CourseInterest: '',
    YourMessage: '',
    isFormShared: 'No',
    isPaymentChecked: 'No'
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const formRef = useRef(null);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear the error for this field when user starts typing
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.FullName.trim()) {
      newErrors.FullName = 'Full name is required';
    }
    
    if (!formData.Phone.trim()) {
      newErrors.Phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.Phone.trim())) {
      newErrors.Phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.CourseInterest) {
      newErrors.CourseInterest = 'Please select a course';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });
    
    try {
      const response = await fetch('https://sheetdb.io/api/v1/snski2y8nb2bu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: [formData]
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSubmitStatus({ 
          success: true, 
          message: 'Thank you! Your message has been sent successfully.' 
        });
        // Reset the form
        setFormData({
          FullName: '',
          Phone: '',
          CourseInterest: '',
          YourMessage: '',
          isFormShared: 'No',
          isPaymentChecked: 'No'
        });
        formRef.current.reset();
      } else {
        throw new Error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        success: false, 
        message: 'There was an error submitting your form. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
      
      // Clear status message after 5 seconds
      setTimeout(() => {
        setSubmitStatus({ success: false, message: '' });
      }, 5000);
    }
  };
  
  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <h2>Contact Us</h2>
          <p>Get in touch with us for your learning journey</p>
        </div>
        
        <div className="contact-container">
          <div className="contact-info" data-aos="fade-right" data-aos-delay="100">
            <h3>Get In Touch</h3>
            <p>Have questions about our courses or need guidance? Fill out the form or reach out to us directly through the contact details below. We're here to help you on your learning journey.</p>
            
            {Object.entries(socialData.contactInfo).map(([key, info]) => {
              const IconComponent = {
                FaPhone, FaEnvelope, FaMapMarkerAlt
              }[info.icon];
              
              return (
                <div key={key} className="info-item">
                  <div className="icon-container">
                    <IconComponent className="info-icon" />
                  </div>
                  <div className="info-content">
                    <h4>{info.label}</h4>
                    {info.url ? (
                      <a href={info.url} className="contact-link">{info.value}</a>
                    ) : (
                      <p>{info.value}</p>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="social-links">
              {socialData.socialLinks.map((social) => {
                const IconComponent = {
                  FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube
                }[social.icon];
                
                return (
                  <a 
                    key={social.name}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="social-link"
                    style={{ '--social-color': social.color }}
                    aria-label={social.name}
                  >
                    <IconComponent className="social-icon" />
                  </a>
                );
              })}
            </div>
          </div>
          
          <div className="contact-form-container" data-aos="fade-left" data-aos-delay="200">
            {submitStatus.message && (
              <div className={`status-message ${submitStatus.success ? 'success' : 'error'}`}>
                {submitStatus.message}
              </div>
            )}
            <form className="contact-form" onSubmit={handleSubmit} ref={formRef}>
                <h3>Send us a Message</h3>
                <p className="form-intro">Fill out the form below and we'll get back to you as soon as possible.</p>
                
                <div className="form-group">
                  <label htmlFor="FullName">Full Name *</label>
                  <input
                    type="text"
                    id="FullName"
                    name="FullName"
                    value={formData.FullName}
                    onChange={handleChange}
                    className={errors.FullName ? 'error' : ''}
                    placeholder="Enter your full name"
                    disabled={isSubmitting}
                  />
                  {errors.FullName && <span className="error-message">{errors.FullName}</span>}
                </div>
                
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="Phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="Phone"
                      name="Phone"
                      value={formData.Phone}
                      onChange={handleChange}
                      className={errors.Phone ? 'error' : ''}
                      placeholder="+91 XXXXXXXXXX"
                      disabled={isSubmitting}
                    />
                    {errors.Phone && <span className="error-message"><i className="fas fa-exclamation-circle"></i> {errors.Phone}</span>}
                  </div>
                  
                  <div className="form-group">
                  <label htmlFor="CourseInterest">Select Course *</label>
                  <select
                    id="CourseInterest"
                    name="CourseInterest"
                    value={formData.CourseInterest}
                    onChange={handleChange}
                    className={errors.CourseInterest ? 'error' : ''}
                    disabled={isSubmitting}
                  >
                    <option value="">-- Select a course --</option>
                    {coursesData.map((course) => (
                      <option key={course.id} value={course.title}>
                        {course.title}
                      </option>
                    ))}
                  </select>
                  {errors.CourseInterest && <span className="error-message">{errors.CourseInterest}</span>}
                </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="YourMessage">Your Message (Optional)</label>
                  <textarea
                    id="YourMessage"
                    name="YourMessage"
                    value={formData.YourMessage}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Tell us about your learning goals and any questions you might have"
                    disabled={isSubmitting}
                  ></textarea>
                </div>
                
                <div className="form-footer">
                  <button 
                    type="submit" 
                    className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="btn-icon" />
                        Send Message
                      </>
                    )}
                  </button>  
                  <p className="form-note">We respect your privacy. Your information will not be shared with third parties.</p>
                </div>
              </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
